<template>
  <div id="lom-events-create" class="professional-nomination-form">
    <!-- Professional Overlay Loading -->
    <v-overlay :value="OverlayLoadingFlag" class="professional-overlay">
      <div class="loading-container">
        <v-progress-circular
          indeterminate
          size="64"
          width="4"
          color="#1976d2"
        ></v-progress-circular>
        <p class="loading-text mt-4">Processing your nomination...</p>
      </div>
    </v-overlay>

    <v-tour name="myTour" :steps="TourSteps" :options="TourOptions"></v-tour>

    <!-- Professional Main Container -->
    <v-card class="main-form-container" elevation="4">
      <!-- Professional Header Section -->
      <v-card-title class="professional-form-header pa-6">
        <div class="header-content">
          <div class="title-section">
            <div class="main-title-wrapper">
              <v-icon size="32" color="#1976d2" class="mr-3"
                >mdi-pencil-circle</v-icon
              >
              <div class="title-text">
                <h1 class="form-main-title">{{ PageTitle }}</h1>
                <div class="status-badge mt-2" v-if="FormStatus">
                  <v-chip color="#28a745" text-color="white" small>
                    <v-icon left size="14">mdi-check-circle</v-icon>
                    {{ FormStatus }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>

          <div class="action-buttons">
            <v-btn
              @click.prevent="backPage"
              color="#ff9800"
              elevation="2"
              rounded
              class="action-btn mr-3 white--text"
            >
              <v-icon left size="18">mdi-arrow-left</v-icon>
              Back
            </v-btn>
            <v-btn
              @click.prevent="refreshPageData"
              color="#2ecc71"
              elevation="2"
              rounded
              class="action-btn white--text"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>
        </div>

        <!-- Professional Description Section -->
        <div class="description-section mt-4" v-if="PageDescription">
          <div class="description-wrapper">
            <v-icon size="20" color="#666" class="mr-2"
              >mdi-information-outline</v-icon
            >
            <p class="form-description">{{ PageDescription }}</p>
          </div>
        </div>
      </v-card-title>

      <!-- Professional Document Dialog -->
      <v-dialog
        v-model="addDocumentRecordPrompt"
        persistent
        max-width="800px"
        transition="dialog-transition"
        overlay-opacity="0.7"
      >
        <add-nomination-documents
          pageTitle="Add Nomination Documents"
          pageDescription="Add Nomination Documents"
          :addDocumentRecordPrompt="addDocumentRecordPrompt"
          @hideAddDocumentRecordPrompt="addNewDocument($event)"
          v-if="addDocumentRecordPrompt"
        ></add-nomination-documents>
      </v-dialog>

      <!-- Professional Content Container -->
      <v-card-text class="form-main-content pa-6">
        <v-container>
          <!-- Professional Member Info Card -->
          <v-card
            class="member-info-card mb-6"
            elevation="3"
            v-if="MemberInfoFlag && Records.MemberName != ''"
          >
            <div class="member-info-header">
              <v-icon size="28" color="white" class="mr-3"
                >mdi-account-circle</v-icon
              >
              <div class="member-info-text">
                <h3 class="member-name">{{ NominationInfo.MemberNameTxt }}</h3>
                <p class="member-designation">
                  {{ NominationInfo.NominationDesignatioNameTxt }}
                </p>
              </div>
            </div>
          </v-card>

          <!-- Professional Loading Section -->
          <div class="form-loading-section" v-if="LoadingFlag">
            <div class="loading-wrapper">
              <v-progress-circular
                indeterminate
                color="#1976d2"
                size="48"
                width="4"
              ></v-progress-circular>
              <p class="loading-text mt-4">Loading form data...</p>
            </div>
          </div>

          <!-- Professional Form Container -->
          <div v-if="!FinalSubmitFlag" class="form-section">
            <v-form
              ref="form1"
              v-model="valid1"
              lazy-validation
              v-on:submit.prevent="confirmAlert"
              class="professional-form"
            >
              <!-- Professional Form Content Wrapper -->
              <div class="form-content-wrapper">
                <!-- Summary Cards Section -->
                <div class="summary-cards-section" v-if="MemberInfoFlag">
                  <NominationSummaryCard
                    :memberInfo="NominationInfo"
                    :lomInfo="LomInfo"
                    :officesHeldData="OfficesHeldData"
                    :lomGrowthData="LomGrowthData"
                    :paymentInfo="PaymentInfo"
                    :showActions="false"
                  />

                  <!-- Member Details Component -->
                  <MemberDetailsCard
                    :memberInfo="NominationInfo"
                    :officesHeldHeader="OfficesHeldHeader"
                    :officesHeldData="OfficesHeldData"
                  />

                  <!-- Organization Details Component -->
                  <OrganizationDetailsCard
                    :lomInfo="LomInfo"
                    :lomGrowthHeader="LomGrowthHeader"
                    :lomGrowthData="LomGrowthData"
                    :paymentInfo="PaymentInfo"
                  />
                </div>

                <!-- Professional Documents Section -->
                <v-card class="documents-card" elevation="3">
                  <v-card-title class="documents-header">
                    <v-icon size="24" color="#1976d2" class="mr-3"
                      >mdi-file-document</v-icon
                    >
                    <span class="documents-title">Required Documents</span>
                    <v-spacer></v-spacer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="#ff9800" v-bind="attrs" v-on="on"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >Click the
                        <v-icon small color="blue">mdi-plus-circle</v-icon>
                        button to add documents</span
                      >
                    </v-tooltip>
                  </v-card-title>

                  <v-card-text>
                    <!-- Professional Instructions -->
                    <div class="instructions-section mb-4">
                      <div class="instruction-card">
                        <v-icon color="#e74c3c" size="20" class="mr-2"
                          >mdi-alert-circle</v-icon
                        >
                        <div class="instruction-content">
                          <h4 class="instruction-title">Upload Guidelines</h4>
                          <ul class="instruction-list">
                            <li>Only PDF file format is accepted</li>
                            <li>Maximum file size: 1 MB per document</li>
                            <li>Ensure all documents are clearly readable</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <!-- Documents Table Section -->
                    <div class="documents-table-section">
                      <div class="add-document-section mb-4">
                        <v-btn
                          color="#1976d2"
                          elevation="2"
                          class="add-document-btn"
                          @click.prevent="addEventPhoto"
                        >
                          <v-icon left>mdi-plus-circle</v-icon>
                          Add Document
                        </v-btn>
                      </div>

                      <v-data-table
                        :headers="tableColumns2"
                        :items="AllDocuments"
                        hide-default-footer
                        disable-sort
                        class="professional-table"
                        id="document-table"
                      >
                        <template v-slot:[`item.DocumentUrl`]="{ item }">
                          <a
                            v-if="item.DocumentUrl"
                            :href="item.DocumentUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="document-link"
                          >
                            <v-icon small color="#1976d2" class="mr-1"
                              >mdi-file-pdf-box</v-icon
                            >
                            View Document
                          </a>
                        </template>
                        <template v-slot:[`item.action`]="{ item, index }">
                          <div class="action-buttons">
                            <v-btn
                              icon
                              small
                              color="#1976d2"
                              @click.prevent="addEventPhoto"
                              class="mr-2"
                            >
                              <v-icon small>mdi-plus-circle</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              small
                              color="error"
                              @click="deleteRow(2, index, item)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </template>
                      </v-data-table>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Professional Questions Section -->
                <div
                  class="questions-section"
                  v-for="(Questions, QuestionsIndex) in Questions"
                  :key="QuestionsIndex"
                >
                  <v-card class="question-category-card" elevation="3">
                    <v-card-title class="question-category-header">
                      <v-icon size="24" color="#1976d2" class="mr-3"
                        >mdi-help-circle</v-icon
                      >
                      <span class="category-title">{{
                        Questions.CategoryName
                      }}</span>
                    </v-card-title>
                    <v-card-text class="question-category-content">
                      <v-row>
                        <v-col
                          cols="12"
                          sm="12"
                          md="6"
                          lg="4"
                          v-for="(
                            Question, QuestionIndex
                          ) in Questions.Questions"
                          :key="QuestionIndex"
                          class="question-item"
                        >
                          <div class="question-wrapper">
                            <!-- Question Label -->
                            <div class="question-label-section">
                              <h4 class="question-title">
                                <span class="required-indicator">*</span>
                                {{ Question.QuestionText }}
                              </h4>
                              <div
                                class="question-constraints"
                                v-if="Question.MaxChar || Question.MaxSize"
                              >
                                <v-chip
                                  v-if="Question.MaxChar"
                                  small
                                  color="#ff9800"
                                  text-color="white"
                                  class="mb-1 mr-2"
                                >
                                  <v-icon left size="12"
                                    >mdi-format-letter-case</v-icon
                                  >
                                  Max {{ Question.MaxChar }} characters
                                </v-chip>
                                <v-chip
                                  v-if="Question.MaxSize"
                                  small
                                  color="#e74c3c"
                                  text-color="white"
                                  class="mb-1"
                                >
                                  <v-icon left size="12"
                                    >mdi-file-upload</v-icon
                                  >
                                  Max {{ Question.MaxSize }} MB
                                </v-chip>
                              </div>
                            </div>

                            <!-- Question Input Fields -->
                            <div class="question-input-section">
                              <!-- Text Input -->
                              <v-text-field
                                v-if="Question.QuestionType == 1"
                                v-model="Question.Answer"
                                :rules="[
                                  validateRequired,
                                  validateMaxChar(Question.MaxChar || 1000),
                                ]"
                                outlined
                                dense
                                required
                                class="professional-input"
                              ></v-text-field>

                              <!-- Textarea -->
                              <v-textarea
                                v-if="Question.QuestionType == 2"
                                v-model="Question.Answer"
                                :rules="[
                                  validateRequired,
                                  validateMaxChar(Question.MaxChar || 1000),
                                ]"
                                outlined
                                dense
                                class="professional-input"
                                rows="4"
                              ></v-textarea>

                              <!-- Select Dropdown -->
                              <v-select
                                v-if="Question.QuestionType == 3"
                                v-model="Question.Answer"
                                :rules="[validateOptions]"
                                :items="Question.OptionArray"
                                outlined
                                dense
                                class="professional-input"
                              ></v-select>

                              <!-- Date Picker -->
                              <v-menu
                                v-if="Question.QuestionType == 4"
                                v-model="Question.DateFlag"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    v-model="Question.Answer"
                                    readonly
                                    outlined
                                    dense
                                    v-bind="attrs"
                                    v-on="on"
                                    class="professional-input"
                                    prepend-inner-icon="mdi-calendar"
                                  ></v-text-field>
                                </template>
                                <v-date-picker
                                  v-model="Question.Answer"
                                  @input="Question.DateFlag = false"
                                ></v-date-picker>
                              </v-menu>

                              <!-- File Upload -->
                              <v-file-input
                                v-if="Question.QuestionType == 5"
                                :rules="[
                                  (value) =>
                                    !value ||
                                    value.size < Question.MaxSize + '000000' ||
                                    'File is required. File size should be less than ' +
                                      Question.MaxSize +
                                      ' MB!',
                                ]"
                                accept="application/pdf"
                                prepend-icon="mdi-paperclip"
                                outlined
                                dense
                                class="professional-input"
                              ></v-file-input>

                              <!-- Time Picker -->
                              <v-menu
                                v-if="Question.QuestionType == 6"
                                ref="menu"
                                v-model="Question.TimeFlag"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                max-width="290px"
                                min-width="290px"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    v-model="Question.Answer"
                                    label="Select Time"
                                    prepend-inner-icon="mdi-clock-outline"
                                    readonly
                                    outlined
                                    dense
                                    v-bind="attrs"
                                    v-on="on"
                                    class="professional-input"
                                  ></v-text-field>
                                </template>
                                <v-time-picker
                                  v-model="Question.Answer"
                                  full-width
                                  @click:minute="
                                    $refs.menu.save(Question.Answer)
                                  "
                                ></v-time-picker>
                              </v-menu>

                              <!-- Radio Group -->
                              <div v-if="Question.QuestionType === 7">
                                <v-radio-group
                                  v-model="Question.Answer"
                                  :rules="[validateOptions]"
                                  dense
                                  row
                                  class="professional-input"
                                >
                                  <v-radio
                                    v-for="(
                                      option, optionIndex
                                    ) in Question.OptionArray"
                                    :key="optionIndex"
                                    :label="option"
                                    :value="option"
                                  ></v-radio>
                                </v-radio-group>
                              </div>

                              <!-- Checkbox Group -->
                              <div
                                v-if="Question.QuestionType == 8"
                                class="checkbox-group"
                              >
                                <v-row>
                                  <v-col
                                    v-for="(
                                      option, optionIndex
                                    ) in Question.OptionArray"
                                    :key="optionIndex"
                                    cols="auto"
                                  >
                                    <v-checkbox
                                      :label="option"
                                      :value="option"
                                      v-model="Question.Answer"
                                      class="professional-checkbox"
                                    ></v-checkbox>
                                  </v-col>
                                </v-row>
                              </div>

                              <!-- URL Input -->
                              <v-text-field
                                v-if="Question.QuestionType == 9"
                                v-model="Question.Answer"
                                label="Enter a valid URL"
                                :rules="[validateUrl]"
                                placeholder="https://example.com"
                                outlined
                                dense
                                class="professional-input"
                                prepend-inner-icon="mdi-link"
                              ></v-text-field>

                              <!-- Number Input -->
                              <div v-if="Question.QuestionType == 10">
                                <v-text-field
                                  type="number"
                                  label=""
                                  outlined
                                  dense
                                  v-model="Question.Answer"
                                  :rules="[
                                    (value) => !!value || 'Must be a number',
                                    (value) =>
                                      !isNaN(value) || 'Must be a number',
                                  ]"
                                  class="professional-input"
                                ></v-text-field>
                              </div>

                              <!-- Rating -->
                              <div
                                class="text-center"
                                v-if="Question.QuestionType == 11"
                              >
                                <div class="rating-wrapper">
                                  <div class="options mb-3">
                                    <div
                                      class="rating-option"
                                      v-for="(
                                        option, index
                                      ) in Question.OptionArray"
                                      :key="index"
                                    >
                                      <label style="display: inline">{{
                                        option
                                      }}</label>
                                    </div>
                                  </div>
                                  <v-rating
                                    background-color="indigo lighten-3"
                                    color="indigo"
                                    v-model="Question.Answer"
                                    :dense="true"
                                    :hover="true"
                                    size="25"
                                    class="rating-stars"
                                  ></v-rating>
                                </div>

                                <div class="mt-2">
                                  <v-chip
                                    small
                                    color="#1976d2"
                                    text-color="white"
                                  >
                                    Selected Rating: {{ Question.Answer || 0 }}
                                  </v-chip>
                                </div>
                              </div>

                              <!-- Image Upload -->
                              <v-file-input
                                v-if="Question.QuestionType == 12"
                                :rules="[
                                  (value) =>
                                    !value ||
                                    value.size < Question.MaxSize + '000000' ||
                                    'Image is required. Image size should be less than ' +
                                      Question.MaxSize +
                                      ' MB!',
                                ]"
                                accept="image/png, image/jpeg, image/bmp"
                                label="Avatar"
                                placeholder="Pick an avatar"
                                prepend-icon="mdi-camera"
                                outlined
                                dense
                                class="professional-input"
                              ></v-file-input>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- Professional Submit Section -->
                <div class="submit-section">
                  <v-card class="submit-card" elevation="3">
                    <v-card-text class="text-center pa-6">
                      <div class="fee-section mb-4">
                        <h3 class="fee-title">Nomination Fee</h3>
                        <v-chip large color="#1976d2" text-color="white">
                          {{ NominationInfo.NominationFeeTxt }}
                        </v-chip>
                      </div>

                      <v-row class="pt-2">
                        <v-col cols="12">
                          <v-checkbox
                            v-model="AgreementChecked1"
                            class="mb-1"
                            label="I hereby declare that the information provided is true and correct to the best of my knowledge."
                          ></v-checkbox>

                          <v-checkbox
                            v-model="AgreementChecked2"
                            class="mt-0"
                            label="I hereby agree to the terms and conditions as per the constitution."
                          ></v-checkbox>
                        </v-col>
                      </v-row>

                      <upload-progress
                        :ProgessStart="progessStart"
                        :UploadPercentage="uploadPercentage"
                      ></upload-progress>

                      <div class="action-buttons mt-6">
                        <v-btn
                          :disabled="!AgreementChecked1 || !AgreementChecked2"
                          id="step12"
                          type="submit"
                          @click.prevent="confirmAlert(2)"
                          :loading="SaveFlag"
                          color="#ff9800"
                          class="submit-btn mr-4 white--text"
                          elevation="2"
                          large
                        >
                          <v-icon left>mdi-content-save</v-icon>
                          Save Application
                        </v-btn>

                        <v-btn
                          v-if="PayFlag"
                          @click.prevent="generateOrderForm"
                          :loading="PaymentFlag"
                          color="#28a745"
                          class="submit-btn mr-4 white--text"
                          elevation="2"
                          large
                        >
                          <v-icon left>mdi-credit-card</v-icon>
                          Pay Now
                        </v-btn>

                        <v-btn
                          :disabled="!AgreementChecked1 || !AgreementChecked2"
                          type="submit"
                          @click.prevent="confirmAlert(1)"
                          :loading="SubmitFlag"
                          color="#1976d2"
                          class="submit-btn white--text"
                          elevation="2"
                          large
                        >
                          <v-icon left>mdi-send</v-icon>
                          Final Submit
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </div>
            </v-form>
          </div>

          <!-- Professional Final Submit Status -->
          <div v-if="FinalSubmitFlag" class="final-submit-section">
            <v-card class="completion-card" elevation="3">
              <v-card-text class="text-center pa-6">
                <div class="completion-icon mb-4">
                  <v-icon size="64" color="#28a745">mdi-check-circle</v-icon>
                </div>
                <h2 class="completion-title mb-3">
                  Final Submission Completed!
                </h2>
                <p class="completion-message">
                  Your nomination has been successfully submitted.
                </p>

                <v-btn
                  v-if="FilePath"
                  :href="FilePath"
                  target="_blank"
                  color="#e74c3c"
                  class="download-btn mt-4"
                  elevation="2"
                  large
                >
                  <v-icon left>mdi-file-pdf-box</v-icon>
                  Download Confirmation
                </v-btn>
              </v-card-text>
            </v-card>
          </div>
        </v-container>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import AddNominationDocuments from "@/view/pages/erp/events/nominate-member/AddNominationDocuments.vue";
import MemberDetailsCard from "@/view/components/nomination/MemberDetailsCard.vue";
import OrganizationDetailsCard from "@/view/components/nomination/OrganizationDetailsCard.vue";
import NominationSummaryCard from "@/view/components/nomination/NominationSummaryCard.vue";

export default {
  mixins: [common],
  components: {
    AddNominationDocuments,
    MemberDetailsCard,
    OrganizationDetailsCard,
    NominationSummaryCard,
  },
  data() {
    return {
      FinalSubmitFlag: false,

      AgreementChecked1: false,
      AgreementChecked2: false,

      TooltipShow1: false,
      ValidateQuesionsFlag: false,
      time: null,
      menu2: false,
      modal2: false,
      menu: false,
      value: 0,

      rating: 4,
      colors: ["red", "orange", "grey", "cyan", "green"],
      labels: ["bad", "so so", "ok", "good", "great"],

      TourOptions: {
        useKeyboardNavigation: false,
        labels: {
          buttonSkip: "Skip",
          buttonPrevious: "Previous",
          buttonNext: "Next",
          buttonStop: "Finish",
        },
      },
      TourSteps: [
        {
          target: "#step1",
          content:
            "Try it, you'll love it!<br>You can put HTML in the steps and completely customize the DOM to suit your needs.",
          params: {
            placement: "top",
          },
        },
        {
          target: "#step2",
          content:
            "Try it, you'll love it!<br>You can put HTML in the steps and completely customize the DOM to suit your needs.",
          params: {
            placement: "top",
          },
        },
        {
          target: "#step3",
          content:
            "Try it, you'll love it!<br>You can put HTML in the steps and completely customize the DOM to suit your needs.",
          params: {
            placement: "top",
          },
        },
      ],

      FromDate: new Date().toISOString().slice(0, 10),
      Modal1: false,

      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      OverlayLoadingFlag: false,
      SaveFlag: false,
      SubmitFlag: false,
      PaymentFlag: false,
      SearchFlag: false,
      NominationInfoLoadingFlag: false, // Add flag to prevent infinite loop
      Text: "",
      TextRules: [(v) => !!v || "This field is required"],
      TitleName: "Enter text here",

      Questions: [],

      FileUpload: false,

      progessStart: 0,
      uploadPercentage: 0,

      JciYearCode: "",

      MemeberFullname: "",
      CurrentDesignation: "",

      LomCode: "",

      LomData: {},
      FilePath: "",
      DateModal1: false,

      EventPhotoImageLimit: 10,

      AllDocuments: [],
      NominationData: {},
      uploadedEventImages: [],
      tableColumns2: [
        { text: "Document Type", value: "DocumentTypeName" },
        { text: "Description", value: "DocumentDescription" },
        { text: "Document", value: "DocumentUrl" },
        { text: "Action", value: "action" },
      ],

      Document: {},
      addDocumentRecordPrompt: false,
      PageDescription: "",
      PageTitle: "",

      UploadedFile1: {},
      NominationDesignationId: "",
      NominationId: "",
      NominationMemberId: "",
      PageInfo: [],
      Records: [],

      PayFlag: true,
      MemberInfoFlag: false,

      // Sample data for UI components (replace with actual data from API)
      NominationInfo: {},

      OfficesHeldHeader: [
        { text: "Year", value: "YearName" },
        { text: "Designation", value: "DesignationName" },
      ],
      OfficesHeldData: [],

      LomInfo: {},

      LomGrowthHeader: [
        { text: "Year", value: "YearName" },
        { text: "Count", value: "Count" },
      ],

      LomGrowthData: [],

      PaymentInfo: {},
      FormStatus: "", // Add missing FormStatus property
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = tr.CurrentJciYearId;
        var YearName = tr.CurrentJciYearName;
        this.YearName = YearName;

        var MonthCode = this.$route.query.month;
        MonthCode = MonthCode == (null || undefined) ? 0 : MonthCode;
        console.log({ MonthCode });
        this.MonthCode = MonthCode;

        var tempDate1 = YearName + "-" + MonthCode + "-01";
        console.log({ tempDate1 });

        var tempDate2 = new Date(tempDate1);
        console.log({ tempDate2 });
        var MonthName = tempDate2.toLocaleString("en-us", { month: "long" });
        console.log({ MonthName });
        this.MonthName = MonthName;

        var MrfStartDate = tr.MrfStartDate;
        console.log({ MrfStartDate });

        // this.CompletedDateMin = MrfStartDate;
        // this.ToDateMin = MrfStartDate;

        var MrfEndDate = tr.MrfEndDate;
        console.log({ MrfEndDate });

        // this.CompletedDateMax = MrfEndDate;
        // this.ToDateMax = MrfEndDate;
        this.pageData();
      }
    },
    // rows: function () {
    //   console.log("watch rows");
    //   var tr = this.rows;

    //   console.log({ tr });
    //   var n1 = Object.keys(tr).length;
    //   console.log({ n1 });
    //   if (n1 > 0) {
    //     this.AllDocuments = tr.NominationDocuments;
    //   }
    // },
  },
  methods: {
    backPage() {
      this.$router.go(-1);
    },
    validateRequired(value) {
      if (this.ValidateQuesionsFlag) {
        return !!value || "This field is required";
      }
      return true;
    },
    validateMaxChar(max) {
      return (value) => {
        if (this.ValidateQuesionsFlag) {
          return value && value.length <= max
            ? true
            : `Max ${max} characters are allowed.`;
        }
        return true;
      };
    },
    validateMinSize(min) {
      return (value) => {
        if (this.ValidateQuesionsFlag) {
          return value && value.length >= min
            ? true
            : `Min ${min} characters are required.`;
        }
        return true;
      };
    },
    validateMaxSize(max) {
      return (value) => {
        if (this.ValidateQuesionsFlag) {
          return value && value.length <= max
            ? true
            : `Answer must not exceed ${max} characters.`;
        }
        return true;
      };
    },
    validateOptions(value) {
      if (this.ValidateQuesionsFlag) {
        return !!value || "Selecting one value is required.";
      }
      return true; // Return true when validation is not required
    },
    validateUrl(value) {
      if (this.ValidateQuesionsFlag) {
        if (!value) {
          return "URL is required.";
        }
        const urlPattern = new RegExp(
          /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}/gm
        );
        return urlPattern.test(value) || "Please enter a valid URL.";
      }
      return true; // Return true when validation is not required
    },
    startTour() {
      console.log("startTour called");
      var TourFlag = this.PageInfo.TourFlag;
      console.log({ TourFlag });
      if (TourFlag == 1) {
        var TourSteps = this.PageInfo.TourSteps;
        console.log({ TourSteps });
        // this.$tours["myTour"].start();
        introJs()
          .setOptions({
            steps: [
              {
                element: document.querySelector("#step1"),
                intro: "Select whether the event is one day or multi day event",
              },
              /*
              {
                element: document.querySelector("#step2"),
                intro: "Select event end date",
              },
              {
                element: document.querySelector("#step3"),
                intro: "Select event venue",
              },
              */
              {
                element: document.querySelector("#step4"),
                intro: "Select the portfolio suitable for each event",
              },
              {
                element: document.querySelector("#step5"),
                intro: "Select the category suitable for each event",
              },
              {
                element: document.querySelector("#step6"),
                intro: "Type the name of your event here",
              },
              {
                element: document.querySelector("#step7"),
                intro: "Choose the 100% efficiency, if your event is eligible",
              },
              {
                element: document.querySelector("#step8"),
                intro:
                  "Write a brief description about the event not exceeding 250 characters",
              },
              {
                element: document.querySelector("#step9"),
                intro:
                  "Upload event images in this section. JPEG and PNG file types are only permitted. Each file should not be more than 1 MB.",
              },
              {
                element: document.querySelector("#step10"),
                intro:
                  "Enter the dignitaries details who participated in the event.",
              },
              {
                element: document.querySelector("#step11"),
                intro:
                  "Upload the pdf document in this section. This is optional.",
              },
              {
                element: document.querySelector("#step12"),
                intro: "Click here to submit the event.",
              },
            ],
          })
          .start();
      }
    },
    addEventPhoto() {
      var n2 = this.AllDocuments.length;
      console.log({ n2 });
      var m2 = this.EventPhotoImageLimit;
      console.log({ m2 });
      if (n2 < m2) {
        this.addDocumentRecordPrompt = true;
      } else {
        var message = "Action photos maximum limit " + m2 + " reached. ";
        this.sweetAlert("error", message, false);
      }
    },
    refreshPageData() {
      console.log("pageData called");
      var NominationDesignationId = this.$route.query.v1;
      NominationDesignationId =
        NominationDesignationId == (null || undefined)
          ? ""
          : NominationDesignationId;
      console.log({ NominationDesignationId });
      this.NominationDesignationId = NominationDesignationId;

      var NominationId = this.$route.query.v2;
      NominationId = NominationId == (null || undefined) ? "" : NominationId;
      console.log({ NominationId });
      this.NominationId = NominationId;

      var NominationMemberId = this.$route.query.v3;
      NominationMemberId =
        NominationMemberId == (null || undefined) ? "" : NominationMemberId;
      console.log({ NominationMemberId });
      this.NominationMemberId = NominationMemberId;

      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "nomination",
        Action: "edit_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getNominationInfo();
      this.getRecordDetails();
      this.getNominationQuestionData();
    },
    getNominationInfo() {
      console.log("getNominationInfo called");

      // Prevent multiple simultaneous calls
      if (this.NominationInfoLoadingFlag) {
        console.log("getNominationInfo already loading, skipping...");
        return;
      }

      this.MemberInfoFlag = false;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nomination-member/nomination-info";
      var upload = {
        UserInterface: 2,
        NominationMember: this.NominationMemberId,
        NominationDesignation: this.NominationDesignationId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("getNominationInfo response=" + JSON.stringify(response));

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log("nomination info records:", { records });

          thisIns.MemberInfoFlag = flag;

          if (flag == 1) {
            // Update NominationInfo with API response
            thisIns.NominationInfo = records.NominationInfo;
            thisIns.OfficesHeldData = records.OfficesHeldData;
            thisIns.OfficesHeldHeader = records.OfficesHeldHeader;
            thisIns.LomInfo = records.LomInfo;
            thisIns.LomGrowthData = records.LomGrowthData;
            thisIns.LomGrowthHeader = records.LomGrowthHeader;
            var paymentInfo = records.PaymentInfo;
            thisIns.PaymentInfo = paymentInfo;
            thisIns.PayFlag =
              paymentInfo.NominationPaymentId > 0 ? false : true;
            // thisIns.toast("success", output);
          }
        })
        .catch(function (error) {
          console.log("getNominationInfo error=" + error);
          thisIns.MemberInfoFlag = false;
        });
    },
    addNewDocument(document) {
      console.log("addNewDocument called");
      this.addDocumentRecordPrompt = false;
      console.log("document=" + JSON.stringify(document));
      this.Document = document;
      this.addNewRow(2);
    },
    addNewRow(tableId) {
      console.log("addNewRow called");

      console.log("tableId=" + tableId);

      switch (tableId) {
        case 2:
          var n2 = this.AllDocuments.length;
          console.log("n2=" + n2);
          var DocumentFlag = this.Document.DocumentFlag;
          console.log("DocumentFlag=" + DocumentFlag);
          var m2 = this.EventPhotoImageLimit;
          console.log({ m2 });
          if (n2 < m2) {
            if (DocumentFlag) {
              this.AllDocuments.push(this.Document);
            }
          } else {
            var message = "Event photos maximum limit " + m2 + " reached. ";
            this.sweetAlert("error", message, false);
          }
          this.Document = {};
          break;

        default:
          break;
      }
    },
    deleteRow(tableId, index, tr) {
      console.log("deleteRow called");
      console.log({ tableId });
      console.log({ index });
      console.log({ tr });
      if (index >= 0) {
        switch (tableId) {
          case 2:
            this.AllDocuments.splice(index, 1);
            break;
          default:
            break;
        }
      }
    },
    getRecordDetails() {
      console.log("getRecordDetails called");

      var NominationId = this.NominationId;
      console.log({ NominationId });

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nomination/form-show";
      var upload = {
        UserInterface: 1,
        Nomination: this.NominationId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );
      console.log("recordData:", this.recordData);

      const thisIns = this;
      var output = "";
      var records = {};
      var table = {};
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          // thisIns.pageLoadingOff()
          thisIns.LoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.AllDocuments = records.NominationDocuments;

            thisIns.FilePath = records.ReportAssetPath1;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
        });
    },
    confirmAlert(status) {
      console.log("confirmAlert called");
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      console.log("status" + status);

      if (validate1) {
        var htmlTxt = "";
        var title = "";
        if (status == 1) {
          title = "Final Submit?";
          htmlTxt +=
            "<h6> You are about to final submit this form. If you click yes, you cannot edit it anymore. Do you want to continue?</h6>";
        } else {
          title = "Save?";
          htmlTxt +=
            "<h6> You are partially saving this form. You can edit it till the deadline. Do you want to continue?</h6>";
        }

        // htmlTxt += "<br/> Title <b> " + TitleName + "</b>";

        Swal.fire({
          title: title,
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            if (status == 1) {
              var PayFlag = this.PayFlag;
              console.log({ PayFlag });
              if (PayFlag) {
                this.sweetAlert(
                  "error",
                  "You are not completed the payment",
                  false
                );
              } else {
                this.submitForm(status);
              }
            } else {
              this.submitForm(status);
            }
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    submitForm(status) {
      console.log("submitForm is called");
      console.log("status", status);

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      var NominationMemberId = this.NominationMemberId;
      console.log({ NominationMemberId });

      var NominationId = this.NominationId;
      console.log({ NominationId });
      // validate1 = false;

      if (validate1) {
        if (status == 1) {
          this.SubmitFlag = true;
        } else {
          this.SaveFlag = true;
        }
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url =
          NominationId > 0 ? "api/nomination/update" : "api/nomination/store";
        add_url = server_url + add_url;
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        var EventData = {
          Nomination: this.NominationId,
          NominationMember: this.NominationMemberId,
          NominationDesignation: this.NominationDesignationId,
          Documents: this.AllDocuments,
          Questions: this.Questions,
          Status: status,
        };

        console.log("EventData=" + JSON.stringify(EventData));

        let upload = new FormData();
        upload.append("UserInterface", 1);
        upload.append("Form", JSON.stringify(EventData));
        upload.append("UploadedFile1", this.UploadedFile1);

        var FileUpload = true;
        console.log("FileUpload=" + FileUpload);

        if (FileUpload) {
          var n2 = this.AllDocuments.length;
          var m2 = 0;
          for (var j = 0; j < n2; j++) {
            var uploadedDocumentFlag = this.AllDocuments[j].DocumentFlag;
            console.log(
              "j=" +
                j +
                ", m2=" +
                m2 +
                ", uploadedDocumentFlag=" +
                uploadedDocumentFlag
            );
            if (uploadedDocumentFlag) {
              var uploadedDocumentId = "ActionPhoto" + m2;
              var tempDocument = this.AllDocuments[j];
              var uploadedDocumentFile = tempDocument.UploadedFile;
              var UploadedDocumentName = tempDocument.UploadedFileName;
              console.log({ tempDocument });
              // var uploadedFile = this.$refs[refName].files[0]
              upload.append(
                uploadedDocumentId,
                uploadedDocumentFile,
                UploadedDocumentName
              );
            }
            m2++;
          }
        }

        console.log("upload=" + JSON.stringify(upload));

        this.progessStart = 1;

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            if (status == 1) {
              thisIns.SubmitFlag = false;
            } else {
              thisIns.SaveFlag = false;
            }
            thisIns.OverlayLoadingFlag = false;
            thisIns.progessStart = 0;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            const data = response.data.data || {};
            console.log({ records });
            console.log({ data });

            if (flag == 1) {
              thisIns.FinalSubmitFlag = status == 1 ? true : false;
              thisIns.sweetAlert("success", output, true);
              thisIns.$router.push("/nominate-member/nomination-apply/list");
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.progessStart = 0;
            if (status == 1) {
              thisIns.SubmitFlag = false;
            } else {
              thisIns.SaveFlag = false;
            }
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        this.sweetAlert("error", output, false);
      }
    },
    generateOrderForm() {
      console.log("generateOrderForm is called");

      var tr = this.NominationInfo;
      console.log({ tr });

      var DesignationId = tr.DesignationId;
      console.log({ DesignationId });

      if (DesignationId > 0) {
        this.OverlayLoadingFlag = true;
        this.PaymentFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/nomination-order/create";
        var upload = {
          UserInterface: 1,
          NominationDesignation: this.NominationDesignationId,
          Nomination: this.NominationId,
          Designation: DesignationId,
          Amount: tr.NominationFee,
        };

        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        // this.$session.set("checkout", upload);
        // this.checkout();

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.OverlayLoadingFlag = false;
            thisIns.PaymentFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.Order = records.Order;
              thisIns.makePayment();
            } else {
              // thisIns.toast("error", output);
              thisIns.sweetAlert("info", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
            thisIns.PaymentFlag = false;
          });
      } else {
        var message = "";
        if (NominationId == 0 || NominationId == null) {
          message += "Save your nomination and then pay the amount";
        }
        this.sweetAlert("error", message, false);
        // this.toast("error", message);
      }
    },
    makePayment() {
      console.log("makePayment");
      // ev.preventDefault();
      var order = this.Order;
      console.log({ order });
      const thisIns = this;
      var rzp1 = new Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description,
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        // callback_url: "http://domain.com/#/about/" + this.order_id,
        redirect: false,
        handler: function (response) {
          console.log("response=" + JSON.stringify(response));
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.PayFlag = false;
          thisIns.AgreementChecked1 = true;
          thisIns.AgreementChecked2 = true;
          thisIns.submitForm(2);
        },
      });
      rzp1.open();
    },
    getNominationQuestionData() {
      console.log("getNominationQuestionData called");

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nomination-question/nomination-questions";
      var upload = {
        UserInterface: 1,
        NominationMember: this.NominationMemberId,
        Nomination: this.NominationId,
        NominationDesignation: this.NominationDesignationId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      this.Questions = [];
      this.OverlayLoadingFlag = true;
      this.SearchFlag = true;
      this.LoadingFlag = true;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SearchFlag = false;
          thisIns.OverlayLoadingFlag = false;
          thisIns.LoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.Questions = records;
            // thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.OverlayLoadingFlag = false;
          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
        });
    },
  },

  beforeMount() {
    this.refreshPageData();
    /*
    this.tableColumns1.push(
      { text: "Description", value: "Description" },
      { text: "Subscription Fees", value: "AmountTxt" },
      { text: "Nos", value: "Qty" },
      { text: "Sub Total", value: "SubTotal" }
      // { text: "Actions", value: "actions" }
    );
    */
  },
  mounted: function () {
    // this.$tours["myTour"].start();
    // this.startTour();
  },
};
</script>
<style lang="scss">
.rating-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.options {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 300px;
}

.rating-option {
  text-align: center;
  flex: 1;
}

.rating-stars {
  margin-top: 8px;
}
#lom-events-create {
  .v-btn--floating {
    position: relative;
  }
  .v-speed-dial--bottom {
    bottom: 12%;
  }
  .v-speed-dial--right {
    right: 5%;
  }
  #faculty-table {
    .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
      font-size: 12px;
      color: red;
    }
    .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
      font-size: 15px;
    }
  }
  .mycard-head-title {
    // padding-bottom: 0;
    font-size: 20px;
    font-family: sans-serif;
    padding: 20px;
    text-align: center;
    word-break: break-word;
    line-height: normal;
    // text-transform: capitalize;
  }
  .mycard-title-1 {
    font-family: sans-serif;
    padding: 10px;
    text-align: left;
    word-break: break-word;
    line-height: normal;
    // text-transform: capitalize;
  }
  .mycard-title-2 {
    font-family: "Roboto", serif;
    padding: 10px;
    text-align: center;
    word-break: break-word;
    line-height: normal;
    // text-transform: capitalize;
  }
  .mycard-text1 {
    font-family: sans-serif;
    // text-transform: capitalize;
  }
  .mycard-text2 {
    font-size: 20px;
    font-family: "Merriweather", serif;
    color: red;
  }
  .notes-text1 {
    padding: 5px;
    margin-left: 2rem;
    font-size: 14px;
    font-family: sans-serif;
  }
  .mycard-text4 {
    font-size: 20px;
    font-family: "EB Garamond", serif;
    font-weight: bold;
    font-style: italic;
  }
  .mycard-text5 {
    font-size: 20px !important;
    font-family: "Roboto", serif;
    color: white;
  }
  .mycard-head-subtitle {
    padding-bottom: 0;
    font-size: 15px;
  }
  .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    font-family: sans-serif;
    font-size: 15px;
  }
  .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    font-family: sans-serif;
    font-size: 14px;
  }
  .animate-button1 {
    color: white;

    animation: heartBeat; /* referring directly to the animation's @keyframe declaration */
    animation-duration: 10s; /* don't forget to set a duration! */
  }
  .notes {
    color: red;
    font-size: 18px;
    font-weight: 500;
    line-height: 2;
  }
  .notes-title {
    font-size: 20px;
    font-weight: bold;
    line-height: 2em;
  }
  .notes-content {
    font-size: 20px;
    font-weight: bold;
    line-height: 2em;
  }
  .fsize-1 {
    font-size: 12px !important;
  }
  .fsize-2 {
    font-size: 14px !important;
  }
  .fsize-3 {
    font-size: 16px !important;
  }
  .fsize-4 {
    font-size: 18px !important;
  }
  .fsize-5 {
    font-size: 20px !important;
  }
  .fcolor-1 {
    color: #fff !important;
  }
  .bg-color-1 {
    background-color: #3a67b1;
  }
  .bg-color-2 {
    background-color: #0097d7;
  }
  .bg-color-3 {
    background-color: #edbe38;
  }
  .bg-color-4 {
    background-color: #56bda3;
  }

  // Professional Form Styles
  &.professional-nomination-form {
    background-color: #f5f7fa;
    min-height: 100vh;

    // Professional Overlay
    .professional-overlay {
      .loading-container {
        text-align: center;
        color: white;

        .loading-text {
          font-size: 1.1rem;
          font-weight: 500;
          margin: 0;
        }
      }
    }

    // Main Form Container
    .main-form-container {
      border-radius: 12px !important;
      overflow: hidden;
      background: white;
      box-shadow: 0 6px 30px rgba(0, 0, 0, 0.1) !important;
    }

    // Professional Header Styles
    .professional-form-header {
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      border-bottom: 1px solid #e3f2fd;

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;

        .title-section {
          flex: 1;

          .main-title-wrapper {
            display: flex;
            align-items: center;

            .title-text {
              .form-main-title {
                font-size: 2rem;
                font-weight: 700;
                color: #2c3e50;
                margin: 0;
                font-family: "Roboto", sans-serif;
                line-height: 1.2;
              }
            }
          }
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;

          .action-btn {
            font-weight: 500;
            text-transform: none;
            padding: 0 20px;
            height: 40px;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }
          }
        }
      }

      .description-section {
        .description-wrapper {
          display: flex;
          align-items: center;
          background: rgba(25, 118, 210, 0.05);
          padding: 1rem;
          border-radius: 8px;
          border-left: 4px solid #1976d2;

          .form-description {
            font-size: 1.1rem;
            color: #424242;
            margin: 0;
            font-weight: 400;
          }
        }
      }
    }

    // Form Content Styles
    .form-main-content {
      background: #f5f7fa;
    }

    // Member Info Card
    .member-info-card {
      border-radius: 12px !important;
      overflow: hidden;

      .member-info-header {
        background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
        color: white;
        padding: 1.5rem;
        display: flex;
        align-items: center;

        .member-info-text {
          .member-name {
            font-size: 1.4rem;
            font-weight: 600;
            margin: 0 0 0.25rem 0;
            font-family: "Roboto", sans-serif;
          }

          .member-designation {
            font-size: 1rem;
            opacity: 0.9;
            margin: 0;
            font-weight: 400;
          }
        }
      }
    }

    // Form Loading
    .form-loading-section {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 200px;

      .loading-wrapper {
        text-align: center;

        .loading-text {
          font-size: 1.1rem;
          color: #666;
          font-weight: 500;
          margin: 0;
        }
      }
    }

    // Professional Form
    .professional-form {
      .form-content-wrapper {
        .summary-cards-section {
          margin-bottom: 2rem;
        }
      }
    }

    // Documents Card
    .documents-card {
      border-radius: 12px !important;
      border: 1px solid #e0e0e0;
      margin-bottom: 2rem;

      .documents-header {
        background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
        border-bottom: 1px solid #e0e0e0;
        padding: 1.5rem;

        .documents-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2c3e50;
        }
      }

      .instructions-section {
        .instruction-card {
          background: #fff3e0;
          border: 1px solid #ffb74d;
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          align-items: flex-start;

          .instruction-content {
            flex: 1;

            .instruction-title {
              font-size: 1rem;
              font-weight: 600;
              color: #e65100;
              margin: 0 0 0.5rem 0;
            }

            .instruction-list {
              margin: 0;
              padding-left: 1rem;

              li {
                font-size: 0.9rem;
                color: #bf360c;
                margin-bottom: 0.25rem;
                line-height: 1.4;
              }
            }
          }
        }
      }

      .documents-table-section {
        .add-document-section {
          .add-document-btn {
            font-weight: 500;
            text-transform: none;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
            }
          }
        }

        .professional-table {
          .document-link {
            color: #1976d2;
            text-decoration: none;
            display: flex;
            align-items: center;

            &:hover {
              text-decoration: underline;
            }
          }

          .action-buttons {
            display: flex;
            gap: 0.5rem;
            justify-content: center;
          }
        }
      }
    }

    // Questions Section
    .questions-section {
      margin-bottom: 2rem;

      .question-category-card {
        border-radius: 12px !important;
        border: 1px solid #e0e0e0;

        .question-category-header {
          background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
          border-bottom: 1px solid #e0e0e0;
          padding: 1.5rem;

          .category-title {
            font-size: 1.3rem;
            font-weight: 600;
            color: #2c3e50;
          }
        }

        .question-category-content {
          padding: 2rem;

          .question-item {
            .question-wrapper {
              .question-label-section {
                margin-bottom: 1rem;

                .question-title {
                  font-size: 1rem;
                  font-weight: 600;
                  color: #2c3e50;
                  margin: 0 0 0.5rem 0;
                  line-height: 1.4;

                  .required-indicator {
                    color: #f44336;
                    margin-right: 0.25rem;
                  }
                }

                .question-constraints {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 0.25rem;
                }
              }

              .question-input-section {
                .professional-input {
                  margin-bottom: 0.5rem;
                }

                .checkbox-group {
                  margin: 0.5rem 0;

                  .professional-checkbox {
                    margin-bottom: 0.25rem;
                  }
                }

                .rating-section {
                  text-align: center;
                  padding: 1rem;
                  background: #f8f9fa;
                  border-radius: 8px;
                  margin: 0.5rem 0;

                  .rating-wrapper {
                    .options {
                      display: flex;
                      justify-content: space-between;
                      margin-bottom: 1rem;

                      .rating-option {
                        .rating-label {
                          font-size: 0.85rem;
                          color: #666;
                          font-weight: 500;
                        }
                      }
                    }

                    .rating-stars {
                      margin: 1rem 0;
                    }

                    .selected-rating {
                      display: flex;
                      justify-content: center;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // Submit Section
    .submit-section {
      margin-top: 2rem;

      .submit-card {
        border-radius: 12px !important;
        border: 2px solid #e3f2fd;
        background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);

        .fee-section {
          .fee-title {
            font-size: 1.4rem;
            font-weight: 600;
            color: #2c3e50;
            margin: 0 0 1rem 0;
          }
        }

        .action-buttons {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1rem;

          .submit-btn {
            min-width: 150px;
            font-weight: 600;
            text-transform: none;
            border-radius: 8px;

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
            }
          }
        }
      }
    }

    // Final Submit Section
    .final-submit-section {
      margin-top: 2rem;

      .completion-card {
        border-radius: 12px !important;
        border: 2px solid #d4edda;
        background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);

        .completion-icon {
          display: flex;
          justify-content: center;
        }

        .completion-title {
          font-size: 1.8rem;
          font-weight: 600;
          color: #28a745;
          margin: 0;
        }

        .completion-message {
          font-size: 1.1rem;
          color: #666;
          margin: 0;
        }

        .download-btn {
          font-weight: 600;
          text-transform: none;
          border-radius: 8px;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(231, 76, 60, 0.3);
          }
        }
      }
    }

    // Responsive Design
    @media (max-width: 1024px) {
      .professional-form-header {
        .header-content {
          flex-direction: column;
          gap: 1rem;

          .action-buttons {
            align-self: flex-start;
          }
        }
      }
    }

    @media (max-width: 768px) {
      .main-form-container {
        margin: 0.5rem;
        border-radius: 8px !important;
      }

      .professional-form-header {
        padding: 1rem !important;

        .header-content
          .title-section
          .main-title-wrapper
          .title-text
          .form-main-title {
          font-size: 1.5rem;
        }
      }

      .form-main-content {
        padding: 1rem !important;
      }

      .action-buttons {
        flex-direction: column;

        .submit-btn {
          width: 100%;
          margin: 0.25rem 0 !important;
        }
      }
    }
  }

  .rating-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .options {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 300px;
  }

  .rating-option {
    text-align: center;
    flex: 1;
  }

  .rating-stars {
    margin-top: 8px;
  }
}
</style>